import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/dashboard');
    setIsMobileMenuOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-[#f6ede5] text-[#4b2f23] border-b border-white/40 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 group">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-tr from-primary-700 to-accent-500 text-white shadow-soft group-hover:shadow-card transition-all duration-200">
              {/* Mountain logo */}
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M3 18h18l-6.5-9.5-3 4-2-3L3 18z" />
              </svg>
            </span>
            <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">
              MountainMerge
            </h1>
          </Link>

          {/* Desktop Navigation Links - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                isActive('/about')
                  ? 'bg-white/60 text-[#4b2f23]'
                  : 'text-[#4b2f23]/80 hover:bg-white/60'
              }`}
            >
              About
            </Link>
            {user ? (
              <>
                <Link
                  to="/classes"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/classes')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Classes
                </Link>
                <Link
                  to="/notes"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/notes')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Notes
                </Link>
                <Link
                  to="/flashcards"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/flashcards')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Flashcards
                </Link>
                <Link
                  to="/study-groups"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/study-groups')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Study Groups
                </Link>
                <Link
                  to="/profile"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/profile')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="ml-4 px-4 py-2 bg-[#4b2f23] text-white font-semibold rounded-lg hover:shadow-lg hover:brightness-110 active:scale-95 transition-all duration-200"
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/classes"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/classes')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Classes
                </Link>
                <Link
                  to="/login"
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive('/login')
                      ? 'bg-white/60 text-[#4b2f23]'
                      : 'text-[#4b2f23]/80 hover:bg-white/60'
                  }`}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="ml-4 px-4 py-2 bg-[#4b2f23] text-white font-semibold rounded-lg hover:shadow-lg hover:brightness-110 active:scale-95 transition-all duration-200"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button - Only visible on mobile */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-[#4b2f23] hover:bg-white/60 transition-all"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              // X icon when menu is open
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              // Hamburger icon (three bars)
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu - Slides down when open */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/40">
            <div className="px-2 pt-2 pb-4 space-y-1">
              <Link
                to="/about"
                onClick={closeMobileMenu}
                className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                  isActive('/about')
                    ? 'bg-white/60 text-[#4b2f23]'
                    : 'text-[#4b2f23]/80 hover:bg-white/60'
                }`}
              >
                About
              </Link>
              {user ? (
                <>
                  <Link
                    to="/classes"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/classes')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Classes
                  </Link>
                  <Link
                    to="/notes"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/notes')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Notes
                  </Link>
                  <Link
                    to="/flashcards"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/flashcards')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Flashcards
                  </Link>
                  <Link
                    to="/study-groups"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/study-groups')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Study Groups
                  </Link>
                  <Link
                    to="/profile"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/profile')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 bg-[#4b2f23] text-white font-semibold rounded-lg hover:shadow-lg hover:brightness-110 active:scale-95 transition-all duration-200"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/classes"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/classes')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Classes
                  </Link>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive('/login')
                        ? 'bg-white/60 text-[#4b2f23]'
                        : 'text-[#4b2f23]/80 hover:bg-white/60'
                    }`}
                  >
                    Log In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={closeMobileMenu}
                    className="block px-4 py-2 bg-[#4b2f23] text-white font-semibold rounded-lg hover:shadow-lg hover:brightness-110 active:scale-95 transition-all duration-200 text-center"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
